import axios from 'axios';
import React, { useState } from 'react';
import { Button }   from '@/components/ui/button';
import { Label }    from '@/components/ui/label';
import { Input }    from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import API_BASE from '@/lib/API_BASE';



// 3 steps: email → otp → new password
const STEP = { EMAIL: 'email', OTP: 'otp', PASSWORD: 'password', DONE: 'done' };

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step,        setStep]        = useState(STEP.EMAIL);
  const [email,       setEmail]       = useState('');
  const [otp,         setOtp]         = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [devOtp,      setDevOtp]      = useState(''); // remove in production
  const [resendTimer, setResendTimer] = useState(0);

  // ── Countdown timer for resend ─────────────────────────────────────────────
  const startTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer(t => {
        if (t <= 1) { clearInterval(interval); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  // ── STEP 1: Send OTP ───────────────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    // if (!email || email.length < 10) {
    //   setError('Enter a valid 10-digit email number.');
    //   return;
    // }
    setLoading(true);
    setError('');

    const fd = new FormData();
    fd.append('action', 'send_otp');
    fd.append('email',  email);

    try {
      const res = await axios.post(`${API_BASE}/forgot-password.php`, fd);
      if (res.data.success) {
        setDevOtp(res.data.dev_otp || ''); // dev only
        setStep(STEP.OTP);
        startTimer();
      } else {
        setError(res.data.error || 'Failed to send OTP.');
      }
    } catch {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── STEP 2: Verify OTP ─────────────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Enter the complete 6-digit OTP.'); return; }
    setLoading(true);
    setError('');

    // Move to password step — actual verify happens on final submit
    setLoading(false);
    setStep(STEP.PASSWORD);
  };

  // ── STEP 3: Reset Password ─────────────────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    setError('');

    const fd = new FormData();
    fd.append('action',   'verify_otp');
    fd.append('email',    email);
    fd.append('otp',      otp);
    fd.append('password', password);

    try {
      const res = await axios.post(`${API_BASE}/forgot-password.php`, fd);
      if (res.data.success) {
        setStep(STEP.DONE);
      } else {
        // If OTP was wrong go back
        setError(res.data.error || 'Reset failed.');
        if (res.data.error?.includes('OTP')) setStep(STEP.OTP);
      }
    } catch {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step meta ──────────────────────────────────────────────────────────────
  const stepMeta = {
    [STEP.EMAIL]:    { title: 'Reset Password',      sub: 'Enter your registered email to receive a reset code.' },
    [STEP.OTP]:      { title: 'Enter OTP',            sub: `We sent a 6-digit code to ${email}` },
    [STEP.PASSWORD]: { title: 'New Password',         sub: 'Choose a strong password for your account.' },
    [STEP.DONE]:     { title: 'Password Reset!',      sub: 'Your password has been updated successfully.' },
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
      <div className="w-full max-w-md">

        {/* Logo + heading */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl">XceedEdge</span>
          </Link>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <h1 className="font-heading text-2xl font-bold">{stepMeta[step].title}</h1>
              <p className="text-muted-foreground text-sm mt-1">{stepMeta[step].sub}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress dots */}
        {step !== STEP.DONE && (
          <div className="flex justify-center gap-2 mb-6">
            {[STEP.EMAIL, STEP.OTP, STEP.PASSWORD].map((s, i) => (
              <div
                key={s}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  s === step
                    ? 'w-6 bg-primary'
                    : [STEP.OTP, STEP.PASSWORD, STEP.DONE].indexOf(step) > i
                      ? 'w-6 bg-primary/40'
                      : 'w-6 bg-muted'
                }`}
              />
            ))}
          </div>
        )}

        <div className="bg-card rounded-2xl border shadow-lg p-6 sm:p-8">

          {/* Error banner */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* ── STEP 1: Email ── */}
          <AnimatePresence mode="wait">
            {step === STEP.EMAIL && (
              <motion.form
                key="email"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
                onSubmit={handleSendOtp}
                className="space-y-4"
              >
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="Enter your registered email"
                    required
                    // maxLength={10}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full h-11" disabled={loading}>
                  {loading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending OTP...</>
                    : 'Send OTP'
                  }
                </Button>
                <Link to="/auth" className="text-sm text-primary hover:underline w-full text-center block mt-2">
                  Back to login
                </Link>
              </motion.form>
            )}

            {/* ── STEP 2: OTP ── */}
            {step === STEP.OTP && (
              <motion.form
                key="otp"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
                onSubmit={handleVerifyOtp}
                className="space-y-4"
              >
                <div className="space-y-4">
                  <div className="text-center">
                    <Label className="text-base font-semibold block mb-2">Verification Code</Label>
                    <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to your email</p>
                    {/* Dev mode OTP hint — remove in production */}
                    {devOtp && (
                      <p className="text-xs text-amber-600 mt-1 bg-amber-50 px-3 py-1 rounded-full inline-block">
                        Dev OTP: {devOtp}
                      </p>
                    )}
                  </div>
                  <div className="flex justify-center py-4">
                    <InputOTP maxLength={6} value={otp} onChange={val => { setOtp(val); setError(''); }}>
                      <InputOTPGroup className="gap-3">
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>

                <Button type="submit" className="w-full h-11" disabled={loading || otp.length !== 6}>
                  {loading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                    : 'Verify OTP'
                  }
                </Button>

                {/* Resend */}
                <div className="text-center text-sm text-muted-foreground">
                  Didn't receive it?{' '}
                  {resendTimer > 0
                    ? <span className="text-muted-foreground">Resend in {resendTimer}s</span>
                    : <button type="button" onClick={handleSendOtp} className="text-primary hover:underline">Resend OTP</button>
                  }
                </div>

                <button
                  type="button"
                  onClick={() => { setStep(STEP.EMAIL); setOtp(''); setError(''); }}
                  className="text-sm text-primary hover:underline w-full text-center block"
                >
                  ← Change email 
                </button>
              </motion.form>
            )}

            {/* ── STEP 3: New Password ── */}
            {step === STEP.PASSWORD && (
              <motion.form
                key="password"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
                onSubmit={handleResetPassword}
                className="space-y-4"
              >
                <div>
                  <Label>New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      placeholder="Min. 6 characters"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    >
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Strength indicator */}
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3].map(n => (
                      <div
                        key={n}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          password.length === 0      ? 'bg-muted'
                          : password.length < 6  && n === 1 ? 'bg-red-400'
                          : password.length < 10 && n <= 2  ? 'bg-amber-400'
                          : password.length >= 10            ? 'bg-green-500'
                          : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {password.length === 0 ? '' : password.length < 6 ? 'Too short' : password.length < 10 ? 'Good' : 'Strong'}
                  </p>
                </div>

                <Button type="submit" className="w-full h-11" disabled={loading || password.length < 6}>
                  {loading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
                    : 'Reset Password'
                  }
                </Button>
              </motion.form>
            )}

            {/* ── STEP 4: Done ── */}
            {step === STEP.DONE && (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center py-6 space-y-4"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-sm text-muted-foreground">
                  You can now log in with your new password.
                </p>
                <Button className="w-full h-11" onClick={() => navigate('/login')}>
                  Go to Login
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}